import { isPlayground } from '@/pages/game/live-events/api/playgroundBackend.js';
import { chatScope } from '@/pages/game/live-events/model/knowledgeChat.js';
import { EVENT_SYSTEM } from '@/pages/game/live-events/model/vocab.js';
import { streamChat } from '@/services/chat/ChatStreamService.js';
import { nullOn404, webRequest } from '../../webRequest.js';
import { runTaskCard, taskContext, TASK_CHAT_PAGE } from '../model/taskChat.js';
import { mockRequest, mockTaskChat } from './mockBackend.js';

const MOCK_CHAT_MS = 1500;

const request = (gameId, req) => (isPlayground() ? mockRequest(gameId, req) : webRequest(gameId, req));
const enc = encodeURIComponent;
const datePath = (instanceId) => `/live-event-instances/${enc(instanceId)}`;

// The overlaps (live events and tests that reach the date's players at the same time) ride beside the plan.
const planCall = async (gameId, instanceId, method, suffix = '', data, params) => {
    const response = await request(gameId, { method, path: `${datePath(instanceId)}/plan${suffix}`, data, params });
    const plan = response?.liveEventRunPlan;
    return plan ? { ...plan, overlaps: response?.overlaps || [] } : null;
};

const taskCall = (gameId, instanceId, taskId, action, data) => planCall(
    gameId, instanceId, 'post', `/tasks/${enc(taskId)}/${action}`, data,
);

// `includeStarted` also lists the dates running now and the ones that ended in the last day, for the Upcoming page.
export const getUpcoming = async (gameId, horizonDays, flywheelId, includeStarted = false) => (await request(gameId, {
    method: 'get',
    path: '/upcoming-live-events',
    params: { horizonDays, ...(flywheelId ? { flywheelId } : {}), ...(includeStarted ? { includeStarted } : {}) },
}))?.upcoming || null;

export const getAutoSettings = async (gameId) => (await request(gameId, {
    method: 'get', path: '/live-event-auto-settings',
}))?.autoSettings || null;

export const saveAutoSettings = async (gameId, data) => (await request(gameId, {
    method: 'put', path: '/live-event-auto-settings', data,
}))?.autoSettings || null;

export const runPassNow = async (gameId, flywheelId) => {
    const pass = await request(gameId, {
        method: 'post', path: '/live-event-auto-settings/run', params: flywheelId ? { flywheelId } : undefined,
    });
    return { started: pass?.started ?? 0, toppedUp: pass?.toppedUp ?? 0 };
};

export const openDate = async (gameId, timelineItemId, occStartMs) => (await request(gameId, {
    method: 'post', path: `/live-event-instances/placements/${enc(timelineItemId)}/occurrences/${occStartMs}`,
}))?.liveEventInstance || null;

export const getDate = async (gameId, instanceId) => (await request(gameId, {
    method: 'get', path: datePath(instanceId),
}))?.liveEventInstance || null;

// Takes back a published date that hasn't started; it can be published again.
export const withdrawDate = async (gameId, instanceId) => (await request(gameId, {
    method: 'post', path: `${datePath(instanceId)}/withdraw`,
}))?.liveEventInstance || null;

export const endDate = async (gameId, instanceId) => (await request(gameId, {
    method: 'post', path: `${datePath(instanceId)}/end-early`,
}))?.liveEventInstance || null;

// The server won't skip a published date, so it is taken back first; put back, it returns unpublished.
export const skipDate = async (gameId, instanceId, reason) => {
    if ((await getDate(gameId, instanceId))?.publishedAt) await withdrawDate(gameId, instanceId);
    return (await request(gameId, {
        method: 'post', path: `${datePath(instanceId)}/skip`, data: { reason },
    }))?.liveEventInstance || null;
};

export const unskipDate = async (gameId, instanceId) => (await request(gameId, {
    method: 'post', path: `${datePath(instanceId)}/unskip`,
}))?.liveEventInstance || null;

export const getTimelineItem = async (gameId, id) => (await request(gameId, {
    method: 'get', path: `/timeline-items/${enc(id)}`,
}))?.timelineItem || null;

export const getFlywheel = async (gameId, id) => (await request(gameId, {
    method: 'get', path: `/flywheels/${enc(id)}`,
}))?.flywheel || null;

export const getPlan = (gameId, instanceId) => nullOn404(() => planCall(gameId, instanceId, 'get'));

// `tasks` is this date's edited copy of the list; without it the list runs as it is.
export const createPlan = (gameId, instanceId, taskListId, tasks) => planCall(gameId, instanceId, 'post', '', { taskListId, ...(tasks ? { tasks } : {}) });

export const resetPlan = (gameId, instanceId) => request(gameId, { method: 'delete', path: `${datePath(instanceId)}/plan` });

export const runPlan = (gameId, instanceId, all = false) => planCall(gameId, instanceId, 'post', '/run', undefined, all ? { all: true } : undefined);

export const runPlanTask = (gameId, instanceId, taskId) => planCall(gameId, instanceId, 'post', '/run', undefined, { taskId });

export const applyChatResult = (gameId, instanceId, taskId, record, saveToEvent) => taskCall(gameId, instanceId, taskId, 'chat-result', {
    revision: record.revision, answers: record.answers || [], saveToEvent, result: record.result,
});

// `answers` and `instruction` only feed the playground's stand-in; intel reads the message itself.
// `onEvent(name, data)` sees every frame as it arrives (the answer as it is written, the steps), for a caller that
// shows the turn while it runs. `messageId` is intel's id for the saved answer, what a like / dislike rates.
export const taskChatTurn = async (gameId, {
    instanceId, taskId, eventId, message, convId, answers, instruction, signal, onStart, onEvent,
}) => {
    if (isPlayground()) {
        await new Promise((resolve) => { setTimeout(resolve, MOCK_CHAT_MS); });
        return { convId: convId || `mock-${taskId}`, messageId: null, ...mockTaskChat(gameId, { instanceId, taskId, answers, instruction }) };
    }
    let reply = '';
    let failure = null;
    let conv = convId;
    let messageId = null;
    const cards = [];
    await streamChat({
        message, convId, page: TASK_CHAT_PAGE, scope: chatScope(eventId), currentPage: 'live-services/upcoming', context: taskContext(instanceId, taskId), gameId,
    }, (name, data) => {
        if (name === 'chat_started') {
            conv = data?.conv_id || conv;
            onStart?.(data?.request_id || null);
        } else if (name === 'step') {
            const card = runTaskCard(data);
            if (card) cards.push(card);
        } else if (name === 'token' && typeof data?.token === 'string') reply += data.token;
        else if (name === 'completed') {
            reply = data?.response || reply;
            messageId = data?.message_id || null;
        } else if (name === 'cancelled') messageId = data?.message_id || messageId;
        else if (name === 'error') failure = data?.message || data?.error || 'The assistant could not answer.';
        onEvent?.(name, data);
    }, { signal });
    if (failure) throw new Error(failure);
    return {
        convId: conv, reply: reply.trim(), cards, messageId,
    };
};

export const addPlanTask = (gameId, instanceId, task) => planCall(gameId, instanceId, 'post', '/tasks', task);

export const reorderPlanTasks = (gameId, instanceId, taskIds) => planCall(gameId, instanceId, 'put', '/tasks/order', { taskIds });

export const editPlanTask = (gameId, instanceId, taskId, task) => planCall(gameId, instanceId, 'put', `/tasks/${enc(taskId)}`, task);

export const dropPlanTask = (gameId, instanceId, taskId, dropped) => taskCall(gameId, instanceId, taskId, dropped ? 'drop' : 'restore');

export const adjustPlanTask = (gameId, instanceId, taskId, instruction) => taskCall(gameId, instanceId, taskId, 'adjust', { instruction });

export const resolvePlanTask = (gameId, instanceId, taskId, resolution) => taskCall(gameId, instanceId, taskId, 'resolve', resolution);

export const stagePlan = (gameId, instanceId) => planCall(gameId, instanceId, 'post', '/stage');

export const publishPlan = (gameId, instanceId) => planCall(gameId, instanceId, 'post', '/publish');

export const getDateConfigs = (gameId, instanceId) => nullOn404(async () => (await request(gameId, {
    method: 'get', path: `${datePath(instanceId)}/configs`,
}))?.dateConfigs || null);

export const getLiveEvent = async (gameId, eventId) => (await request(gameId, {
    method: 'get', path: `/live-events/${enc(eventId)}`,
}))?.liveEvent || null;

export const listTaskLists = async (gameId, eventId) => (await request(gameId, {
    method: 'get', path: '/live-event-task-lists', params: { beatId: eventId },
}))?.liveEventTaskLists || [];

export const listSnippets = async (gameId, eventId) => (await request(gameId, {
    method: 'get', path: '/game-knowledge-snippets', params: { system: EVENT_SYSTEM, systemId: eventId },
}))?.gameKnowledgeSnippets || [];

export const createSnippet = async (gameId, snippet) => (await request(gameId, {
    method: 'post', path: '/game-knowledge-snippets', data: snippet,
}))?.gameKnowledgeSnippet || null;

export const listRuns = async (gameId, eventId) => (await request(gameId, {
    method: 'get', path: `/live-events/${enc(eventId)}/runs`,
}))?.liveEventRuns || [];

export const listConfigFiles = async (gameId) => (await request(gameId, { method: 'get', path: '/game-config/files' })) || [];

export const listSegments = async (gameId) => (await request(gameId, { method: 'get', path: '/player-segment' }))?.segments || [];
